import React, { useMemo } from 'react';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import ReportSectionHead from './ReportSectionHead';
import StatisticalDataTable, { statNumberColumn, statPercentColumn, statRankColumn } from './StatisticalDataTable';
import {
  ClinicalBarChart,
  ClinicalDoughnutChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import {
  computeShares,
  doughnutChartData,
  horizontalBarChartData,
  topNWithOthers,
  truncateLabel
} from './charts/chartDataHelpers';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

export default function HeadDoctorReportsMedicamentsPage() {
  const { data, loading } = useClinicalReports();
  const rows = useMemo(
    () => computeShares(data?.medications?.rows || [], 'prescription_count'),
    [data]
  );

  const chartRows = rows.slice(0, 10);

  const barData = useMemo(
    () =>
      horizontalBarChartData(
        chartRows.map((r) => truncateLabel(r.medication_name, 26)),
        chartRows.map((r) => Number(r.prescription_count)),
        COPY.CHART_PRESCRIPTION_COUNT
      ),
    [chartRows]
  );

  const pieData = useMemo(() => {
    const pie = topNWithOthers(rows, 'prescription_count', 'medication_name', 5, COPY.CHART_OTHERS);
    return doughnutChartData(pie.labels, pie.values, pie.colors);
  }, [rows]);

  const quantityPie = useMemo(() => {
    const pie = topNWithOthers(rows, 'total_quantity', 'medication_name', 5, COPY.CHART_OTHERS);
    return doughnutChartData(pie.labels, pie.values, pie.colors);
  }, [rows]);

  const tableColumns = [
    {
      key: 'rank',
      label: '#',
      className: 'text-muted',
      render: (_row, idx) => idx + 1
    },
    {
      key: 'medication_name',
      label: COPY.COL_MEDICATION,
      className: 'fw-semibold',
      render: (row) => row.medication_name
    },
    statRankColumn(COPY.COL_PRESCRIPTIONS, 'prescription_count'),
    statPercentColumn(COPY.COL_SHARE),
    {
      key: 'total_quantity',
      label: COPY.COL_QUANTITY,
      className: 'text-end',
      render: (row) =>
        `${Number(row.total_quantity || 0).toLocaleString('fr-FR')}${row.unit_name ? ` ${row.unit_name}` : ''}`
    },
    statNumberColumn(COPY.COL_LINES, 'line_count')
  ];

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_MEDICAMENTS}
        title={COPY.PAGE_MEDICAMENTS_TITLE}
        description={COPY.PAGE_MEDICAMENTS_DESC}
      />

      {!loading && rows.length > 0 && (
        <>
          <ReportsChartGrid>
            <ReportsChartCol lg={7}>
              <ReportChartCard
                title={COPY.CHART_MED_BAR_TITLE}
                subtitle={COPY.CHART_MED_BAR_DESC}
                height={Math.max(280, chartRows.length * 36 + 80)}
              >
                <ClinicalBarChart
                  data={barData}
                  horizontal
                  xLabel={COPY.CHART_AXIS_ORDONNANCES}
                />
              </ReportChartCard>
            </ReportsChartCol>
            <ReportsChartCol lg={5}>
              <ReportChartCard
                title={COPY.CHART_MED_PIE_TITLE}
                subtitle={COPY.CHART_MED_PIE_DESC}
                height={Math.max(280, chartRows.length * 36 + 80)}
              >
                <ClinicalDoughnutChart data={pieData} />
              </ReportChartCard>
            </ReportsChartCol>
          </ReportsChartGrid>

          <ReportsChartGrid>
            <ReportsChartCol lg={12}>
              <ReportChartCard
                title={COPY.CHART_MED_QTY_PIE_TITLE}
                subtitle={COPY.CHART_MED_QTY_PIE_DESC}
                height={300}
              >
                <ClinicalDoughnutChart data={quantityPie} cutout="52%" />
              </ReportChartCard>
            </ReportsChartCol>
          </ReportsChartGrid>
        </>
      )}

      <h3 className="hd-reports-table-title">{COPY.TABLE_DETAIL_TITLE}</h3>
      <StatisticalDataTable
        columns={tableColumns}
        rows={rows}
        rowKey="medication_id"
        maxValueKey="prescription_count"
        loading={loading}
        emptyLabel={COPY.EMPTY_MEDICATIONS}
        loadingLabel={COPY.LOADING}
      />
    </>
  );
}
